import { AndOperatorLabel } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview/AndOperatorLabel';
import { WhereClauseField } from '@modules/common/components/WhereClauseField';
import { CompareHighlightWrapper } from '@modules/quantify/components/CompareHighlightWrapper';
import {
  CombinedDashboardFilterField,
  CombinedQuantifyFilterField,
} from '@modules/quantify/components/QuantifyQueryBuilder/Fields/CombinedDashboadQuantifyFilterFields';
import {
  useFetchQuantifyReport,
  useGetQuantifyWhereClause,
  useTriggerQuantifyQueryAnalyticsEvent,
} from '@modules/quantify/hooks';
import { QuantifyActionType } from '@modules/quantify/store/quantifyActionType';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyQuery } from '@modules/quantify/store/types';
import {
  combineWhereClauseWithMetadataFilterIfRequired,
  isValidQuery,
  shouldUpdateDenominatorOnFilterChange,
} from '@modules/quantify/utils';
import { FilterListRounded } from '@mui/icons-material';
import { Box, Button, Stack, Typography } from '@mui/material';
import { isEqual } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import {
  EmptyWhereClause,
  WhereClause,
} from 'v2/core/services/queryBuilder/whereClause';

export function QuantifyWhereClauseField() {
  const dispatch = useQuantifyStore(({ dispatch }) => dispatch);
  const query = useQuantifyStore(({ query }) => query);
  const shouldHighlightWhereClauseField = useQuantifyStore(
    ({ visual }) => visual.shouldHighlightWhereClauseField,
  );
  const primaryWhereClause = useGetQuantifyWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const primaryDenominatorWhereClause = useGetQuantifyWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const dashboardWhereClause = useQuantifyStore(
    ({ visual }) => visual.dashboardFilters?.whereClause,
  );

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const fetchReportData = useFetchQuantifyReport();
  const quantifyQueryAnalyticsEvent = useTriggerQuantifyQueryAnalyticsEvent();
  const [shouldFetchReportData, setShouldFetchReportData] = useState(false);

  const isValidQueryState = isValidQuery(query);

  useEffect(() => {
    if (shouldFetchReportData && isValidQueryState) {
      setShouldFetchReportData(false);
      fetchReportData();
      quantifyQueryAnalyticsEvent('Quantify - Query Executed');
    }
  }, [
    fetchReportData,
    isValidQueryState,
    quantifyQueryAnalyticsEvent,
    shouldFetchReportData,
  ]);

  const onChange = useCallback(
    ({
      whereClause: updatedWhereClause,
    }: {
      whereClause: WhereClause | null;
    }) => {
      const queryPayload: Partial<QuantifyQuery> = {
        whereClause: updatedWhereClause ?? undefined,
        shouldShowTopDataPoints: true,
        visibleDataPoints: [],
      };

      if (
        shouldUpdateDenominatorOnFilterChange({
          groupBy: query.groupByField,
          whereClause: primaryWhereClause,
          denominatorWhereClause:
            primaryDenominatorWhereClause ?? EmptyWhereClause,
          formulaID: query.formulaQuery?.eqlFunction,
        })
      ) {
        queryPayload.formulaQuery = {
          ...query.formulaQuery,
          denominatorWhereClause:
            combineWhereClauseWithMetadataFilterIfRequired({
              whereClause: updatedWhereClause ?? EmptyWhereClause,
              groupBy: query.groupByField,
            }),
        };
      }

      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: queryPayload,
      });

      dispatch({
        type: QuantifyActionType.SET_QUERY,
        payload: queryPayload,
      });

      setShouldFetchReportData(true);

      try {
        quantifyQueryAnalyticsEvent('Quantify - Add Query Via Query Builder', {
          queryString: mapToEQL({
            whereClause: updatedWhereClause ?? EmptyWhereClause,
            expressionType: ExpressionType.Records,
            sourceDetailsMap: sourceDetailsMap ?? new Map(),
          }),
        });

        setShouldFetchReportData(true);
      } catch {}
    },
    [
      query.groupByField,
      query.formulaQuery,
      primaryWhereClause,
      primaryDenominatorWhereClause,
      dispatch,
      quantifyQueryAnalyticsEvent,
      sourceDetailsMap,
    ],
  );

  const isDisabled = !query.groupByField;
  const isEmptyQuery =
    !primaryWhereClause || isEqual(EmptyWhereClause, primaryWhereClause);

  const renderWhereClauseField = useCallback(() => {
    if (!dashboardWhereClause) {
      return (
        <WhereClauseField
          whereClause={primaryWhereClause}
          onChange={onChange}
          isDisabled={isDisabled}
          label={null}
          modalTestId="quantify-where-clause-modal"
        />
      );
    }

    return (
      <Stack>
        <CombinedDashboardFilterField
          onRemove={() => setShouldFetchReportData(true)}
        />
        <AndOperatorLabel />
        <CombinedQuantifyFilterField
          whereClause={primaryWhereClause}
          onChange={onChange}
          label={null}
          modalTestId="quantify-where-clause-modal"
          onClear={() => {
            onChange({ whereClause: null });
          }}
        />
      </Stack>
    );
  }, [dashboardWhereClause, isDisabled, onChange, primaryWhereClause]);

  return (
    <CompareHighlightWrapper isActive={shouldHighlightWhereClauseField}>
      <Stack
        px={2}
        py={1.5}
        spacing={1.5}
        data-testid="quantify-where-clause-field"
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack spacing={1} direction="row" alignItems="center">
            <FilterListRounded
              sx={{
                color: 'text.secondary',
              }}
            />
            <Typography variant="subtitle1">Filter</Typography>
          </Stack>
          {!isEmptyQuery && !dashboardWhereClause && (
            <Button
              variant="text"
              size="small"
              disabled={isDisabled}
              onClick={() => {
                onChange({ whereClause: null });
              }}
            >
              Clear
            </Button>
          )}
        </Stack>
        <Box sx={{ pl: 4 }}>{renderWhereClauseField()}</Box>
      </Stack>
    </CompareHighlightWrapper>
  );
}
